import { Section, SectionHeading, FadeIn } from "@/components/Section";
import { CreditCard, FileText, RefreshCcw, Banknote, ShieldAlert } from "lucide-react";

const CAPABILITIES = [
  { icon: FileText, title: "Invoice coordination", desc: "Automated invoice generation across programs, vendors, and billing cycles." },
  { icon: Banknote, title: "Vendor payment workflows", desc: "Standardized payout scheduling and documentation for vendor relationships." },
  { icon: CreditCard, title: "ACH / card enablement", desc: "Patient and B2B payment rails enabled through vetted third-party processors." },
  { icon: RefreshCcw, title: "Reconciliation dashboards", desc: "Program-level reconciliation, variance tracking, and finance-ready exports." },
];

export default function FinancialWorkflow() {
  return (
    <Section testId="financial-section" className="bg-slate-50 border-y border-slate-200">
      <div className="grid lg:grid-cols-12 gap-12 items-start" data-nosnippet="true">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Financial Infrastructure"
            title="Operational payment infrastructure — without custody or complexity."
            intro="Nodera enables the financial workflows behind cash-pay programs without acting as a bank, billing company, or fund custodian."
          />

          <FadeIn delay={0.2}>
            <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-700">
                <ShieldAlert className="w-3.5 h-3.5" /> Critical disclaimers
              </div>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {[
                  "Platform does not custody funds",
                  "Not a bank or billing company",
                  "Payments handled by third-party providers",
                  "Clinic pays vendor directly",
                ].map((d, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-600" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {CAPABILITIES.map((c, i) => (
              <FadeIn key={i} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center">
                    <c.icon className="w-4.5 h-4.5 text-slate-900" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 font-display text-base font-semibold text-slate-900">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Decorative flow diagram */}
          <FadeIn delay={0.2}>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">Operational flow</div>
              <div className="mt-4 grid grid-cols-5 items-center gap-2 text-center text-[11px] text-slate-600">
                {["Patient", "Clinic", "Nodera", "Vendor", "Processor"].map((n, i) => (
                  <div key={i} className="relative">
                    <div className="mx-auto w-10 h-10 rounded-full bg-slate-900 text-white font-display font-bold text-xs flex items-center justify-center">
                      {i + 1}
                    </div>
                    <div className="mt-2 font-semibold text-slate-800">{n}</div>
                    {i < 4 && (
                      <div className="hidden sm:block absolute top-5 -right-1/2 w-full h-px bg-slate-200" />
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-500 text-center">
                Nodera coordinates workflows and data between parties — funds move via clinic ↔ vendor and patient ↔ processor rails.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </Section>
  );
}
